import { useEffect, useState } from 'react';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FR } from '../../components/ui/FieldRow.jsx';
import { isoVersDate } from '../../api/mappers';
import { tvaApi } from '../../api/resources';
import { PERIODES_TVA, bornesTva } from '../../lib/periodes-tva';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Le trimestre en cours pour l'exercice en cours, l'année entière sinon. */
function periodeParDefaut(annee) {
  const maintenant = new Date();
  if (maintenant.getFullYear() !== annee) return 'annee';
  return 't' + (Math.floor(maintenant.getMonth() / 3) + 1);
}

const SORTES = {
  facture: '🧾',
  avoir: '↩️',
  ventes: '🛒',
  depense: '💳',
  fournisseur: '🏗',
};

/**
 * Position TVA : combien a été facturé, combien se récupère sur les achats,
 * combien reste à payer.
 *
 * <p>Le calcul est fait par le serveur sur toutes les pièces de la période :
 * factures, avoirs, ventes de caisse, dépenses et factures fournisseurs des
 * chantiers. Sans réponse du serveur (hors ligne), {@code repli} s'affiche.
 *
 * @param {object} props
 * @param {number} props.annee exercice affiché
 * @param {string|number} props.version change quand une pièce est ajoutée, pour recalculer
 * @param {import('react').ReactNode} props.repli décompte local, quand le serveur ne répond pas
 */
export function PositionTva({ annee, version, repli }) {
  const [periode, setPeriode] = useState(() => periodeParDefaut(annee));
  const [position, setPosition] = useState(null);
  const [echec, setEchec] = useState(false);
  const [detail, setDetail] = useState(false);

  useEffect(() => {
    setPeriode(periodeParDefaut(annee));
  }, [annee]);

  useEffect(() => {
    let actif = true;
    const [debut, fin] = bornesTva(annee, periode);
    setEchec(false);
    const lis = () =>
      tvaApi
        .position(debut, fin)
        .then((p) => {
          if (actif) setPosition(p);
        })
        .catch(() => {
          if (actif) setEchec(true);
        });
    lis();
    // Une pièce tout juste modifiée part au serveur un instant plus tard :
    // une seconde lecture la compte.
    const relecture = setTimeout(lis, 1500);
    return () => {
      actif = false;
      clearTimeout(relecture);
    };
  }, [annee, periode, version]);

  const choix = (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 11 }}>
      {PERIODES_TVA.map(([k, l]) => {
        const on = periode === k;
        return (
          <div
            key={k}
            onClick={() => setPeriode(k)}
            style={{
              flex: '1 0 22%',
              textAlign: 'center',
              cursor: 'pointer',
              border: '1px solid ' + (on ? C.ink : C.border),
              background: on ? C.ink : 'transparent',
              color: on ? '#fff' : C.ink,
              borderRadius: 9,
              padding: '8px 4px',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {l}
          </div>
        );
      })}
    </div>
  );

  if (echec) {
    return <div>{repli}</div>;
  }
  if (!position) {
    return (
      <div>
        {choix}
        <div style={{ fontSize: 11, color: C.muted, padding: 12 }}>Calcul de la TVA…</div>
      </div>
    );
  }

  const fa = position.facturee;
  const re = position.recuperable;
  const solde = +position.solde || 0;
  const aPayer = solde >= 0;
  const montant = (v) => DEVISE + ' ' + fmt(+v || 0);
  // Les dépenses dont la TVA compte sans ticket joint (notées depuis le relevé
  // bancaire, par exemple) : le décompte les signale au lieu de les taire.
  const sansJustificatif = (position.lignes || []).filter(
    (l) => l.sansJustificatif && +l.tva > 0,
  ).length;

  return (
    <div>
      {choix}
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 9 }}>
        {trf('Du {0} au {1}', isoVersDate(position.debut), isoVersDate(position.fin))} ·{' '}
        {position.methode === 'recues' ? 'selon les paiements reçus' : 'selon les factures émises'}
      </div>

      <Card style={{ marginBottom: 11 }}>
        <Bar
          icon="🧾"
          title="TVA facturée"
          color={C.teal}
          sub="Factures, avoirs et ventes de caisse"
        />
        <div style={{ padding: 13 }}>
          <FR label="TVA sur les factures" value={montant(fa.factures)} />
          {+fa.avoirs !== 0 && (
            <FR
              label="moins la TVA des avoirs"
              value={'− ' + montant(Math.abs(fa.avoirs))}
              color={C.muted}
            />
          )}
          {+fa.ventes !== 0 && (
            <FR label="TVA sur les ventes de caisse" value={montant(fa.ventes)} />
          )}
          {(fa.parTaux || [])
            .filter((t) => +t.ht !== 0 || +t.tva !== 0)
            .map((t) => (
              <FR
                key={t.taux}
                label={`${+t.taux} % · base ${montant(t.ht)}`}
                value={montant(t.tva)}
                color={C.muted}
              />
            ))}
          <FR label="Total TVA facturée" value={montant(fa.total)} color={C.teal} bold last />
        </div>
      </Card>

      <Card style={{ marginBottom: 11 }}>
        <Bar
          icon="💳"
          title="TVA à récupérer"
          color={C.green}
          sub="Matériel, outillage, frais et factures fournisseurs"
        />
        <div style={{ padding: 13 }}>
          <FR label="TVA sur les dépenses" value={montant(re.depenses)} />
          <FR label="TVA sur les factures fournisseurs" value={montant(re.facturesFournisseurs)} />
          <FR
            label="Total des achats (TVA comprise)"
            value={montant(re.achatsTtc)}
            color={C.muted}
          />
          <FR label="Total TVA à récupérer" value={montant(re.total)} color={C.green} bold last />
        </div>
      </Card>

      <Card style={{ marginBottom: 11 }}>
        <div
          style={{
            padding: 16,
            textAlign: 'center',
            background: aPayer ? C.redL : C.greenL,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: C.grey }}>
            {aPayer ? 'TVA à payer' : 'TVA à récupérer'}
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: aPayer ? C.red : C.green }}>
            {montant(Math.abs(solde))}
          </div>
          <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>
            {trf('{0} facturée − {1} à récupérer', montant(fa.total), montant(re.total))}
          </div>
        </div>
      </Card>

      {(re.sansDroit > 0 || sansJustificatif > 0) && (
        <div
          style={{
            background: C.tealL,
            border: '1px solid ' + C.teal + '28',
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 9,
            color: C.grey,
            marginBottom: 11,
            lineHeight: 1.5,
          }}
        >
          {sansJustificatif > 0 && (
            <div>
              {trf(
                'Dépenses sans justificatif : {0}. Leur TVA est comptée, mais l’AFC ne l’admet en déduction que sur quittance : joins le ticket dans Dépenses.',
                sansJustificatif,
              )}
            </div>
          )}
          {re.sansDroit > 0 && (
            <div>
              {trf(
                'Achats sans TVA à récupérer (loyer, assurance, frais bancaires…) : {0}.',
                re.sansDroit,
              )}
            </div>
          )}
        </div>
      )}

      <div
        onClick={() => setDetail((d) => !d)}
        style={{ fontSize: 11, fontWeight: 700, color: C.navy, cursor: 'pointer', marginBottom: 8 }}
      >
        {detail ? '▾' : '▸'} {trf('Voir le détail ({0})', (position.lignes || []).length)}
      </div>
      {detail && (
        <Card style={{ marginBottom: 11 }}>
          <div style={{ padding: '6px 12px' }}>
            {(position.lignes || []).map((l, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'baseline',
                  padding: '6px 0',
                  borderBottom: '1px solid ' + C.border,
                  fontSize: 10,
                }}
              >
                <span>{SORTES[l.sorte] || '•'}</span>
                <span style={{ color: C.muted, minWidth: 62 }}>{isoVersDate(l.date)}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {l.sorte === 'ventes' ? `Ventes de caisse (${l.libelle})` : l.libelle}
                </span>
                <span style={{ color: C.muted }}>{montant(l.montant)}</span>
                <span
                  style={{
                    fontWeight: 700,
                    minWidth: 70,
                    textAlign: 'right',
                    color: l.sorte === 'depense' || l.sorte === 'fournisseur' ? C.green : C.teal,
                  }}
                >
                  {montant(l.tva)}
                  {l.estimee ? ' *' : ''}
                </span>
                {/* Notée depuis le relevé bancaire, sans ticket : sa TVA compte,
                    mais l'AFC ne l'admet en déduction que sur quittance. */}
                {l.sansJustificatif && +l.tva > 0 && (
                  <span style={{ color: C.amber, fontSize: 9, fontWeight: 700 }}>
                    {tr('Sans justificatif')}
                  </span>
                )}
              </div>
            ))}
            {(position.lignes || []).some((l) => l.estimee) && (
              <div style={{ fontSize: 9, color: C.muted, padding: '6px 0' }}>
                * TVA calculée au taux, faute d'avoir été saisie.
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
